/**
 * LinkSection (P15.3 / #416).
 *
 * Generic primitive for managing a junction table from within a parent
 * entity's drawer. Used by:
 *
 *   - Product drawer -> ProductIBSLink (link Products to IBSes)
 *   - Product drawer -> ProductCapabilityLink (link Products to Capabilities)
 *   - IBS drawer    -> IBSChannelLink (link IBSes to Channels)
 *
 * Renders a list of currently-linked rows + a picker to add a new
 * link + per-row remove buttons. POSTs / DELETEs against the
 * supplied endpoints; invalidates the supplied query key on success.
 *
 * The link payload is intentionally minimal at v0.15.0 -- the target
 * id plus an optional `role` (for ProductIBSLink) or `notes` is the
 * common shape. Customise per-call via the `targetFieldKey` +
 * `optionalFields` props if a richer payload is needed later.
 */
import { Identifier } from "shared/components/Identifier";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useOptionalToast } from "shared/toast/ToastProvider";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "shared/api/client";

export interface LinkRow {
  id?: string;
  /** The target entity id (e.g. ibs_service_id, capability_id, channel_id). */
  [key: string]: unknown;
}

export interface LinkOption {
  id: string;
  code: string;
  name: string;
  [key: string]: unknown;
}

/**
 * P23.2 (#524): config for an inline editable field on each link row.
 * When supplied, the row renders a small numeric input alongside the
 * remove button; pressing Save fires a PATCH against the per-row
 * endpoint. Used today for `revenue_share` on ProductIBSLink; could
 * be reused for any future per-link scalar (e.g. a freshness SLA
 * threshold).
 */
export interface LinkSectionEditableField {
  /** Key on the LinkRow + PATCH body for the editable value. */
  key: string;
  /** Short label rendered to the left of the input. */
  label: string;
  /** PATCH endpoint pattern; the row's target id is substituted in. */
  patchEndpoint: (targetId: string) => string;
  /** Render a raw value as the input's text content. */
  format: (raw: unknown) => string;
  /**
   * Parse user-typed text into the PATCH body value. Returning
   * `undefined` skips the PATCH (e.g. malformed input).
   */
  parse: (text: string) => unknown;
  /** Optional placeholder text. */
  placeholder?: string;
  /**
   * Address the PATCH by the LINK row id rather than by the id of the
   * thing it points at -- the same distinction `deleteByLinkId` draws.
   * Needed where one endpoint serves several junctions and so cannot
   * key on a parent path.
   */
  byLinkId?: boolean;
}

export interface LinkSectionProps {
  /** i18n key namespace, e.g. "modules.foundation.admin.products.ibsLinks". */
  i18nPrefix: string;
  /** GET + POST endpoint for the nested link list. */
  listEndpoint: string;
  /** DELETE endpoint pattern; `{targetId}` is substituted at click time. */
  deleteEndpoint: (targetIdOrLinkId: string) => string;
  /** Endpoint to fetch the available targets (e.g. /v1/admin/foundation/ibses). */
  optionsEndpoint: string;
  /** Field name on the LinkRow + POST body that carries the target id. */
  targetFieldKey: string;
  /** Display field on the LinkRow if the link itself carries metadata (e.g. "role"). */
  displayFieldKey?: string;
  /** React Query key for cache invalidation. */
  queryKey: readonly unknown[];
  /** Options query key (caches the list-of-options across drawer mounts). */
  optionsQueryKey: readonly unknown[];
  /**
   * P400: an optional scalar captured ALONGSIDE the target and sent on
   * create -- e.g. a subcontractor's `role`. "Who" without "what" does
   * not support a judgement, so a link carrying meaning needs to
   * capture it at the point the link is made, not afterwards.
   */
  createExtraFieldKey?: string;
  /**
   * P429.2: when the extra field is a closed vocabulary (a regulatory
   * link's `relation`), offer it as a select rather than free text, so
   * the form cannot send a value the API will refuse.
   */
  createExtraOptions?: readonly { value: string; label: string }[];
  /**
   * P428: the extra value is REQUIRED -- a group membership carries its
   * rationale, and the API refuses one without. Add stays disabled
   * until it is given, rather than sending a request that must fail.
   */
  createExtraRequired?: boolean;
  /**
   * P429.2: narrow the options offered -- only a Critical Internal
   * Function can fund a service, so the funder picker lists no others.
   */
  optionsFilter?: (option: LinkOption) => boolean;
  /** Identifier used when DELETE goes via link.id (not target id). */
  deleteByLinkId?: boolean;
  /**
   * P23.2 (#524): optional inline-editable scalar per row. Renders a
   * small input + Save button; PATCHes the supplied endpoint on
   * confirm.
   */
  editableField?: LinkSectionEditableField;
  /**
   * Round 16 O7 -- the register's own voice, opt-in so the other link
   * sections read as before. A visible label for the extra field (it
   * had a placeholder and no label); a sentence beside a disabled Add
   * saying what it waits for; a removal that asks first; and a toast
   * for an add and a removal.
   */
  extraLabelKey?: string;
  addHintKey?: string;
  confirmRemove?: boolean;
  toastKeys?: { added: string; removed: string };
  /** Round 18 G26 -- other lists the link changes (a group's member
   *  count in the table behind the drawer read "0" until a reload). */
  alsoInvalidate?: readonly (readonly unknown[])[];
}

export function LinkSection({
  i18nPrefix,
  listEndpoint,
  deleteEndpoint,
  optionsEndpoint,
  alsoInvalidate,
  targetFieldKey,
  createExtraFieldKey,
  createExtraOptions,
  optionsFilter,
  createExtraRequired = false,
  displayFieldKey,
  queryKey,
  optionsQueryKey,
  deleteByLinkId = false,
  editableField,
  extraLabelKey,
  addHintKey,
  confirmRemove = false,
  toastKeys,
}: LinkSectionProps) {
  const { t } = useTranslation();
  const toast = useOptionalToast();
  const [confirming, setConfirming] = useState<string | null>(null);
  // Round 18 G26 -- a link row may carry its own name and code (an
  // IBS group's members do). The picker's options are only what can
  // still be added, so a member already in the group is not among
  // them: the drawer printed "c10652c7..." and the toast "removed from
  // the group." with no name. The row's own fields come second.
  const rowLabel = (row: LinkRow | undefined): { name?: string; code?: string } => ({
    ...(typeof row?.name === "string" ? { name: row.name } : {}),
    ...(typeof row?.code === "string" ? { code: row.code } : {}),
  });
  const rowFor = (id: string): LinkRow | undefined =>
    (Array.isArray(links.data) ? links.data : []).find(
      (r) => String(r[targetFieldKey]) === id,
    );
  const nameOf = (id: string) =>
    (Array.isArray(options.data) ? options.data : []).find((o) => o.id === id)?.name ??
    rowLabel(rowFor(id)).name ??
    "";
  const qc = useQueryClient();
  const [pickerValue, setPickerValue] = useState("");
  const [extraValue, setExtraValue] = useState(createExtraOptions?.[0]?.value ?? "");

  const links = useQuery({
    queryKey,
    queryFn: () => api.get<LinkRow[]>(listEndpoint),
    staleTime: 15_000,
  });
  const options = useQuery({
    queryKey: optionsQueryKey,
    queryFn: () => api.get<LinkOption[]>(optionsEndpoint),
    staleTime: 5 * 60_000,
  });

  const linkedIds = useMemo(
    () => new Set((links.data ?? []).map((r) => String(r[targetFieldKey]))),
    [links.data, targetFieldKey],
  );
  const availableOptions = useMemo(
    () =>
      (Array.isArray(options.data) ? options.data : []).filter(
        (o) => !linkedIds.has(o.id) && (optionsFilter ? optionsFilter(o) : true),
      ),
    [options.data, linkedIds, optionsFilter],
  );

  const addMut = useMutation({
    mutationFn: (id: string) =>
      api.post<LinkRow>(listEndpoint, {
        [targetFieldKey]: id,
        ...(createExtraFieldKey && extraValue.trim()
          ? { [createExtraFieldKey]: extraValue.trim() }
          : {}),
      }),
    onSuccess: (_row, id) => {
      if (toastKeys && toast) {
        toast.push({
          tone: "success",
          message: t(toastKeys.added, { name: nameOf(id) }),
        });
      }
      setPickerValue("");
      setExtraValue(createExtraOptions?.[0]?.value ?? "");
      void qc.invalidateQueries({ queryKey });
      void qc.invalidateQueries({ queryKey: optionsQueryKey });
      for (const k of alsoInvalidate ?? []) void qc.invalidateQueries({ queryKey: k });
    },
  });

  const removeMut = useMutation({
    mutationFn: (key: string) => api.delete<void>(deleteEndpoint(key)),
    onSuccess: (_v, key) => {
      if (toastKeys && toast) {
        toast.push({
          tone: "success",
          message: t(toastKeys.removed, { name: nameOf(key) }),
        });
      }
      setConfirming(null);
      // Round 17 G20 -- the row goes at once: the drawer still listed a
      // member under its own "removed" toast until the refetch landed.
      qc.setQueryData<LinkRow[]>(queryKey, (old) =>
        Array.isArray(old) ? old.filter((r) => String(r[targetFieldKey]) !== key) : old,
      );
      void qc.invalidateQueries({ queryKey });
      void qc.invalidateQueries({ queryKey: optionsQueryKey });
      for (const k of alsoInvalidate ?? []) void qc.invalidateQueries({ queryKey: k });
    },
  });

  const patchMut = useMutation({
    mutationFn: (args: { targetId: string; body: Record<string, unknown> }) =>
      editableField
        ? api.patch<LinkRow>(editableField.patchEndpoint(args.targetId), args.body)
        : Promise.resolve({} as LinkRow),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey });
    },
  });

  const onAdd = () => {
    if (!pickerValue) return;
    addMut.mutate(pickerValue);
  };

  return (
    <section data-testid={`link-section-${targetFieldKey}`}>
      <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">
        {t(`${i18nPrefix}.heading`)}
      </h3>

      {links.data && links.data.length === 0 && (
        <p data-testid="link-section-empty" className="text-xs text-slate-500 mb-2">
          {t(`${i18nPrefix}.empty`)}
        </p>
      )}

      <ul className="space-y-1 mb-3">
        {(links.data ?? []).map((row) => {
          const targetId = String(row[targetFieldKey]);
          const opt =
            (options.data ?? []).find((o) => o.id === targetId) ?? rowLabel(row);
          const deleteKey = deleteByLinkId && row.id ? String(row.id) : targetId;
          return (
            <li
              key={String(row.id ?? targetId)}
              data-testid="link-section-row"
              data-target-id={targetId}
              className="flex items-baseline justify-between gap-2 text-xs bg-white border border-slate-200 rounded px-2 py-1"
            >
              {/* ROUND 13 -- the separators are TEXT, not margin. `ms-2`
                  put a gap on screen and nothing in innerText, so the
                  name and the code read as one token to a screen reader
                  and to anything copying the row into a board pack.
                  Round 12 H5's class, in a shared component every link
                  drawer renders; it surfaced the moment a fixture
                  carried both a name and a code. */}
              <span className="min-w-0 truncate">
                <span className="font-medium text-slate-900">
                  {opt?.name ?? targetId.slice(0, 8) + "..."}
                </span>
                {opt?.code && (
                  <>
                    {" · "}
                    {/* Round 17 I16 -- a key, isolated as one, so it
                        reads whole inside a right-to-left row. */}
                    <span className="text-slate-500">
                      <Identifier>{opt.code}</Identifier>
                    </span>
                  </>
                )}
                {displayFieldKey &&
                  row[displayFieldKey] !== undefined &&
                  row[displayFieldKey] !== null && (
                    <>
                      {" · "}
                      <span className="text-slate-600">
                        [
                        {createExtraFieldKey === displayFieldKey && createExtraOptions
                          ? (createExtraOptions.find(
                              (o) => o.value === row[displayFieldKey],
                            )?.label ?? String(row[displayFieldKey]))
                          : String(row[displayFieldKey])}
                        ]
                      </span>
                    </>
                  )}
              </span>
              {editableField && (
                <EditableInline
                  initial={editableField.format(row[editableField.key])}
                  label={editableField.label}
                  placeholder={editableField.placeholder}
                  onSave={(text) => {
                    const parsed = editableField.parse(text);
                    if (parsed === undefined) return;
                    patchMut.mutate({
                      // Some per-link fields are addressed by the LINK
                      // row rather than by the thing it points at --
                      // the same distinction `deleteByLinkId` draws.
                      targetId:
                        editableField.byLinkId && row.id ? String(row.id) : targetId,
                      body: { [editableField.key]: parsed },
                    });
                  }}
                />
              )}
              {confirmRemove && confirming === deleteKey ? (
                <span data-testid="link-section-confirm" className="flex gap-2">
                  <span className="text-slate-700">
                    {t(`${i18nPrefix}.confirmRemove`)}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeMut.mutate(deleteKey)}
                    data-testid="link-section-confirm-yes"
                    className="text-red-600 hover:underline"
                  >
                    {t(`${i18nPrefix}.remove`)}
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirming(null)}
                    data-testid="link-section-confirm-no"
                    className="text-slate-600 hover:underline"
                  >
                    {t(`${i18nPrefix}.cancel`)}
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() =>
                    confirmRemove
                      ? setConfirming(deleteKey)
                      : removeMut.mutate(deleteKey)
                  }
                  data-testid="link-section-remove"
                  className="text-red-600 hover:underline"
                >
                  {t(`${i18nPrefix}.remove`)}
                </button>
              )}
            </li>
          );
        })}
      </ul>

      <div className="flex items-center gap-2">
        <select
          value={pickerValue}
          onChange={(e) => setPickerValue(e.target.value)}
          data-testid="link-section-picker"
          // Round 18 G26 -- a long service name sized the select past the
          // drawer's edge; it may shrink.
          className="min-w-0 max-w-full flex-1 rounded border border-slate-300 px-2 py-1 text-sm"
          disabled={options.isLoading}
        >
          <option value="">{t(`${i18nPrefix}.pickerPlaceholder`)}</option>
          {availableOptions.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name} ({o.code})
            </option>
          ))}
        </select>
        {createExtraFieldKey && createExtraOptions && (
          <select
            value={extraValue}
            onChange={(e) => setExtraValue(e.target.value)}
            data-testid="link-section-extra"
            aria-label={t(`${i18nPrefix}.extraPlaceholder`)}
            className="w-40 rounded border border-slate-300 px-2 py-1 text-sm"
          >
            {createExtraOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        )}
        {createExtraFieldKey && !createExtraOptions && extraLabelKey && (
          <label className="flex flex-col text-xs text-slate-600">
            {t(extraLabelKey)}
            <input
              dir="auto"
              type="text"
              value={extraValue}
              onChange={(e) => setExtraValue(e.target.value)}
              data-testid="link-section-extra"
              className="w-56 rounded border border-slate-300 px-2 py-1 text-sm"
            />
          </label>
        )}
        {createExtraFieldKey && !createExtraOptions && !extraLabelKey && (
          <input
            dir="auto"
            type="text"
            value={extraValue}
            onChange={(e) => setExtraValue(e.target.value)}
            data-testid="link-section-extra"
            placeholder={t(`${i18nPrefix}.extraPlaceholder`)}
            className="w-40 rounded border border-slate-300 px-2 py-1 text-sm"
          />
        )}
        <button
          type="button"
          onClick={onAdd}
          data-testid="link-section-add"
          disabled={
            !pickerValue ||
            addMut.isPending ||
            (createExtraRequired && !extraValue.trim())
          }
          className="px-3 py-1.5 rounded bg-brand-brass text-brand-navy text-xs font-medium hover:bg-brand-brassDark disabled:opacity-50"
        >
          {t(`${i18nPrefix}.add`)}
        </button>
      </div>
      {addHintKey && (!pickerValue || (createExtraRequired && !extraValue.trim())) && (
        <p data-testid="link-section-add-hint" className="mt-1 text-xs text-slate-500">
          {t(addHintKey)}
        </p>
      )}
    </section>
  );
}

/**
 * P23.2 (#524): tiny inline-edit widget driven by `editableField`
 * config. Stays uncontrolled-ish: keeps a local draft, fires onSave
 * when the user clicks the check.
 */
function EditableInline({
  initial,
  label,
  placeholder,
  onSave,
}: {
  initial: string;
  label: string;
  placeholder: string | undefined;
  onSave: (text: string) => void;
}) {
  const [draft, setDraft] = useState<string>(initial);
  const dirty = draft !== initial;
  return (
    <span
      data-testid="link-section-editable-field"
      className="flex items-center gap-1 text-[10px] text-slate-600"
    >
      <span className="uppercase tracking-wide">{label}</span>
      <input
        dir="auto"
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder={placeholder}
        data-testid="link-section-editable-input"
        className="w-16 rounded border border-slate-300 px-1 py-0.5 text-[10px] tabular-nums"
      />
      <button
        type="button"
        onClick={() => onSave(draft)}
        disabled={!dirty}
        data-testid="link-section-editable-save"
        className="text-brand-brassDark disabled:opacity-30 hover:text-brand-brass"
        aria-label="Save"
        title="Save"
      >
        ✓
      </button>
    </span>
  );
}
